import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LongTextarea } from "@/components/LongText";
import { EuroInput } from "@/components/EuroInput";

// Tipi che richiedono un esito "Com'è andata?" a fine appuntamento.
export const ESITO_TIPI = ["vendita", "acquisizione", "sopralluogo", "acquirente"];

// Mappa tipo -> tipo di esito (DEVE restare allineata a ESITO_KIND nel backend).
// acquisizione = prendere l'incarico (valutazione), acquirente = visita, vendita/sopralluogo = generico sì/no.
export const ESITO_KIND = { vendita: "generico", acquirente: "visita", acquisizione: "valutazione", sopralluogo: "generico" };

export const ESITO_MOTIVI = {
  valutazione: [["prezzo_alto", "Prezzo richiesto troppo alto"], ["altra_agenzia", "Ha scelto un'altra agenzia"], ["vende_privato", "Vuole vendere da privato"], ["non_vende", "Non vende più / rimanda"], ["altro", "Altro"]],
  visita: [["prezzo", "Prezzo"], ["zona", "Zona"], ["piano_esposizione", "Piano / esposizione"], ["stato_lavori", "Stato immobile / lavori"], ["metratura", "Metratura / distribuzione"], ["deve_vendere", "Deve prima vendere casa sua"], ["altro", "Altro"]],
  generico: [["altro", "Altro"]],
};

// Appuntamento "finito" = ora attuale oltre la fine (data + durata).
export const apptFinished = (e) => {
  if (!e || !e.data) return false;
  const end = new Date(e.data).getTime() + (parseInt(e.durata_min) || 60) * 60000;
  return end <= Date.now();
};

// L'appuntamento ha bisogno dell'esito: tipo valido, finito, non annullato, esito non ancora registrato.
export const apptNeedsEsito = (e) =>
  !!e && e.kind !== "bloccato" && e.tipo !== "bloccato" && ESITO_TIPI.includes(e.tipo)
  && !e.esito_registrato && e.stato !== "annullato" && apptFinished(e);

// Controlli dell'esito riutilizzabili (inline nel modulo appuntamento e dentro la modale).
export function EsitoFields({ appt, onSaved, onCancel }) {
  const kind = ESITO_KIND[appt.tipo] || "generico";
  const [positivo, setPositivo] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [nota, setNota] = useState("");
  const [extra, setExtra] = useState({ ricontattare: false, ricontatto_data: "", acquirente_attivo: true });
  const [saving, setSaving] = useState(false);
  const [prezzo, setPrezzo] = useState("");
  useEffect(() => {
    if (kind === "valutazione") api.get(`/appuntamenti/${appt.id}/prezzo-suggerito`).then((r) => { if (r.data?.prezzo) setPrezzo(r.data.prezzo); }).catch(() => {});
  }, [appt.id, kind]);
  const motivi = ESITO_MOTIVI[kind] || [["altro", "Altro"]];
  const posLabel = kind === "valutazione" ? "Incarico preso" : kind === "visita" ? "Proposta presa" : "Sì, svolto";
  const negLabel = kind === "valutazione" ? "Incarico non preso" : kind === "visita" ? "Proposta non presa" : "No";
  const notaVisibile = positivo === false && (motivo === "altro" || kind === "generico");
  const save = async () => {
    if (positivo === null) return toast.error("Indica com'è andata");
    const body = { positivo };
    if (positivo && kind === "valutazione") {
      if (!(parseFloat(prezzo) > 0)) return toast.error("Inserisci il prezzo richiesto dell'immobile");
      body.prezzo = parseFloat(prezzo);
    }
    if (!positivo) {
      if (kind !== "generico" && !motivo) return toast.error("Seleziona un motivo");
      body.motivo = motivo || "altro";
      body.nota = nota;
      if (kind === "valutazione") { body.ricontattare = extra.ricontattare; body.ricontatto_data = extra.ricontatto_data; }
      if (kind === "visita") body.acquirente_attivo = extra.acquirente_attivo;
    }
    setSaving(true);
    try { await api.post(`/appuntamenti/${appt.id}/esito`, body); toast.success("Esito registrato"); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };
  return (
    <div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button type="button" data-testid="esito-positivo" onClick={() => setPositivo(true)} className={`py-2.5 rounded-xl text-sm font-semibold border ${positivo === true ? "bg-emerald-500 text-white border-transparent" : "bg-white border-[var(--border)]"}`}>✅ {posLabel}</button>
        <button type="button" data-testid="esito-negativo" onClick={() => setPositivo(false)} className={`py-2.5 rounded-xl text-sm font-semibold border ${positivo === false ? "bg-red-500 text-white border-transparent" : "bg-white border-[var(--border)]"}`}>❌ {negLabel}</button>
      </div>
      {positivo === true && kind === "valutazione" && (
        <div className="mb-3">
          <label className="text-[11px] uppercase text-[var(--muted)] font-semibold">Prezzo richiesto dell'immobile (€) *</label>
          <EuroInput testid="esito-prezzo" value={prezzo} onChange={(e) => setPrezzo(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm" placeholder="Es. 250.000" />
        </div>
      )}
      {positivo === false && (
        <div className="space-y-2 mb-3">
          <label className="text-[11px] uppercase text-[var(--muted)] font-semibold">Motivo</label>
          <select data-testid="esito-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm">
            <option value="">— Seleziona —</option>
            {motivi.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {notaVisibile && <LongTextarea data-testid="esito-nota" value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota (obbligatoria)" className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm" rows={2} />}
          {kind === "valutazione" && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="esito-ricontattare" checked={extra.ricontattare} onChange={(e) => setExtra({ ...extra, ricontattare: e.target.checked })} /> Da ricontattare
              {extra.ricontattare && <input type="date" data-testid="esito-ricontatto-data" value={extra.ricontatto_data} onChange={(e) => setExtra({ ...extra, ricontatto_data: e.target.value })} className="ml-2 px-2 py-1 rounded border border-[var(--border)] text-sm" />}
            </label>
          )}
          {kind === "visita" && (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="esito-acq-attivo" checked={extra.acquirente_attivo} onChange={(e) => setExtra({ ...extra, acquirente_attivo: e.target.checked })} /> Acquirente ancora attivo</label>
          )}
        </div>
      )}
      <div className="flex gap-2">
        {onCancel && <button type="button" onClick={onCancel} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">Annulla</button>}
        <button type="button" data-testid="esito-salva" onClick={save} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-[#111111] text-white font-semibold text-sm disabled:opacity-50">{saving ? "Salvo…" : "Salva esito"}</button>
      </div>
    </div>
  );
}

// Modale "Com'è andata?" usata nelle liste (home Oggi, agenda mobile, appuntamenti di oggi).
export function EsitoModal({ appt, onClose, onSaved }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="esito-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md card-immo p-6 max-h-[calc(100vh-2rem)] overflow-y-auto">
        <h3 className="font-display text-lg font-bold mb-1">Com'è andata?</h3>
        <p className="text-sm text-slate-500 mb-4">{appt.titolo}</p>
        <EsitoFields appt={appt} onSaved={onSaved} onCancel={onClose} />
      </div>
    </div>
  );
}
